import React, { useState, useCallback, useRef } from 'react';
import { Typography, Toolbar, ListItemText, Menu, MenuItem, AppBarProps } from '@mui/material';
import { ArrowDropDown } from '@mui/icons-material';

const MENU_ITEM_HEIGHT = 48;

export type ToolbarMenuItem = {
    label: string;
    onClick: Function;
};
export type DropdownToolbarProps = AppBarProps & {
    title: string;
    subtitleLabel: string | undefined;
    menuItems: ToolbarMenuItem[] | undefined;
    navigationIcon?: JSX.Element;
};

export const DropdownToolbar = (props: DropdownToolbarProps): JSX.Element => {
    const { title, subtitleLabel, menuItems, navigationIcon } = props;
    const [anchorEl, setAnchorEl]: [Element | null, Function] = useState(null);
    const anchor = useRef(null);
    const _navigationIcon = useCallback(() => {
        if (navigationIcon) {
            return <div style={{ marginRight: 32, cursor: 'pointer' }}>{navigationIcon}</div>;
        }
    }, [navigationIcon]);

    return (
        <>
            <Toolbar data-testid="dropdown-toolbar">
                {_navigationIcon()}
                <ListItemText
                    data-testid="dropdown-title"
                    id={'dropdown-toolbar-text'}
                    primary={
                        <Typography variant="h6" style={{ fontWeight: 600, lineHeight: 1 }}>
                            {title}
                        </Typography>
                    }
                    secondary={
                        <Typography
                            id={'dropdown-subtitle'}
                            data-testid={'dropdown-subtitle'}
                            ref={anchor}
                            aria-haspopup="true"
                            style={{ display: 'inline-flex', cursor: 'pointer' }}
                            component={'div'}
                            onClick={(): void => {
                                setAnchorEl(anchor.current);
                            }}
                        >
                            {subtitleLabel || ''}
                            {menuItems && menuItems.length > 0 && <ArrowDropDown style={{ marginLeft: 4 }} />}
                        </Typography>
                    }
                />
                {props.children}
            </Toolbar>

            {menuItems && Boolean(anchorEl) && (
                <Menu
                    id="dropdown-toolbar-menu"
                    data-testid="dropdown-toolbar-menu"
                    elevation={0}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={(): void => setAnchorEl(null)}
                    MenuListProps={{ style: { padding: 0 } }}
                    PaperProps={{
                        style: {
                            maxHeight: MENU_ITEM_HEIGHT * 5.5,
                            width: 300,
                        },
                    }}
                >
                    {menuItems.map((item: ToolbarMenuItem, ind: number) => (
                        <MenuItem
                            key={`Toolbar-Option-${ind}`}
                            id={`menu-item-${ind}`}
                            data-testid={`menu-item-${ind}`}
                            onClick={(): void => {
                                setAnchorEl(null);
                                item.onClick();
                            }}
                            style={{ whiteSpace: 'break-spaces' }}
                        >
                            {item.label}
                        </MenuItem>
                    ))}
                </Menu>
            )}
        </>
    );
};
